import {
  NotificationData,
  NotificationReadData,
  UnreadCountData,
} from '@/services/NotificationCenter/NotificationCenterService'
import { FluentIcon } from '@fluentui/react-icons'
import { EventPayloadId } from './notificationMetadataTypes'

// ================================
// CORE NOTIFICATION TYPES
// ================================

/**
 * Priority levels for notifications
 * 0 = LOW, 1 = NORMAL, 2 = MEDIUM, 3 = HIGH
 */
export type NotificationPriority = 0 | 1 | 2 | 3

/**
 * Status of a notification
 */
export type NotificationStatus = 'unread' | 'read'

// Re-export EventPayloadId for consumers of this module
export { EventPayloadId }

/**
 * Notification types for different events handled by the Notification Center
 */
export type NotificationType = EventPayloadId | string

/**
 * Type guard to check if a string is a valid EventPayloadId
 */
export function isKnownNotificationType(type: string): type is EventPayloadId {
  return Object.values(EventPayloadId).includes(type as EventPayloadId)
}

/**
 * Core notification interface
 * Represents a single notification in the system
 */
export interface Notification {
  /** Unique identifier (UUID) */
  id: string

  /** Type of notification */
  type: NotificationType

  /** Notification title/subject */
  title: string

  /** Notification content/body */
  content: string

  /** Additional metadata (flexible object for type-specific data) */
  metadata: Record<string, unknown>

  /** Current status */
  status: NotificationStatus

  /** When it was marked as read (null if unread) */
  read_at: string | null

  /** Creation timestamp */
  created_at: string

  /** Expiration timestamp */
  expires_at: string

  /** Source entity type (e.g., 'case', 'referral') */
  source_type: string | null

  /** Source entity ID */
  source_id: string | null

  /** Priority level */
  priority: NotificationPriority
}

// ================================
// API REQUEST/RESPONSE TYPES
// ================================

/**
 * Query parameters for GET /notification-center/
 */
export interface NotificationListParams {
  /** Page number (default: 1) */
  page?: number

  /** Items per page (max: 100, default: 20) */
  per_page?: number

  /** Sort field */
  sort?: 'created_at' | 'title' | 'type' | 'priority' | 'status'

  /** Sort order */
  order?: 'asc' | 'desc'

  /** Filter by status */
  status?: NotificationStatus

  /** Filter by notification type */
  type?: NotificationType

  /** Filter by priority */
  priority?: NotificationPriority

  /** Filter by created date (after) */
  created_after?: string

  /** Filter by created date (before) */
  created_before?: string
}

/**
 * Response from GET /notification-center/
 */
export interface NotificationListResponse {
  /** Array of notifications */
  notifications: Notification[]

  /** Total number of notifications (across all pages) */
  total: number

  /** Current page number */
  page: number

  /** Items per page */
  per_page: number

  /** Total number of pages */
  total_pages: number
}

/**
 * Response from GET /notification-center/unread-count
 */
export interface UnreadCountResponse {
  /** Number of unread notifications */
  unread_count: number
}

/**
 * Request body for PUT /notification-center/{id}/read
 */
export interface MarkAsReadRequest {
  /** Optional timestamp (defaults to now) */
  read_at?: string
}

/**
 * Response from PUT /notification-center/{id}/read
 * Returns the notification object directly (not wrapped)
 */
export type MarkAsReadResponse = Notification

/**
 * Response from PUT /notification-center/read-all
 */
export interface MarkAllAsReadResponse {
  /** Success message */
  message: string

  /** Number of notifications marked as read */
  count: number
}

/**
 * Response from DELETE /notification-center/{id}
 */
export interface DeleteNotificationResponse {
  /** Success message */
  message: string
}

// ================================
// PUSHER EVENT TYPES
// ================================

/**
 * Pusher channel format: private-user-{userId}
 */
export type PusherChannelName = `private-user-${string}`

/**
 * Pusher event types for real-time notifications
 */
export enum PusherEventType {
  NEW_NOTIFICATION = 'notification.new',
  NOTIFICATION_READ = 'notification.read',
  NOTIFICATION_DELETED = 'notification.deleted',
  BULK_READ = 'notification.bulk_read',
}

/**
 * Pusher event payload for new notification
 */
export interface PusherNewNotificationEvent {
  type: PusherEventType.NEW_NOTIFICATION
  notification: Notification
}

/**
 * Pusher event payload for notification marked as read
 */
export interface PusherNotificationReadEvent {
  type: PusherEventType.NOTIFICATION_READ
  notification_id: string
  read_at: string
}

/**
 * Pusher event payload for notification deleted
 */
export interface PusherNotificationDeletedEvent {
  type: PusherEventType.NOTIFICATION_DELETED
  notification_id: string
}

/**
 * Pusher event payload for bulk read operation
 */
export interface PusherBulkReadEvent {
  type: PusherEventType.BULK_READ
  updated_count: number
  notification_ids: string[]
}

/**
 * Union type for all Pusher events
 */
export type PusherEvent =
  | PusherNewNotificationEvent
  | PusherNotificationReadEvent
  | PusherNotificationDeletedEvent
  | PusherBulkReadEvent

// ================================
// STATE MANAGEMENT TYPES
// ================================

/**
 * Notification center state interface
 */
export interface NotificationState {
  /** Array of notifications */
  notifications: Notification[]

  /** Number of unread notifications */
  unreadCount: number

  /** Whether notifications have been initially loaded */
  hasInitiallyLoaded: boolean

  /** Loading states */
  loading: {
    /** Initial load or refresh */
    fetching: boolean

    /** Loading more notifications (pagination) */
    loadingMore: boolean

    /** Marking as read */
    markingAsRead: boolean

    /** Deleting notification */
    deleting: boolean

    /** Bulk operations */
    bulkOperating: boolean
  }

  /** Error states */
  error: {
    /** General error message */
    message: string | null

    /** Error code */
    code: string | null

    /** Retry available */
    retryable: boolean
  } | null

  /** Pagination info */
  pagination: {
    /** Current page */
    currentPage: number

    /** Items per page */
    perPage: number

    /** Total items across all pages */
    totalItems: number

    /** Total pages */
    totalPages: number

    /** Whether more pages are available */
    hasMore: boolean
  }

  /** Filters */
  filters: {
    /** Status filter */
    status?: NotificationStatus

    /** Type filter */
    type?: NotificationType

    /** Priority filter */
    priority?: NotificationPriority

    /** Date range filters */
    dateRange?: {
      start: string
      end: string
    }
  }

  /** Sort configuration */
  sort: {
    /** Field to sort by */
    field: NotificationListParams['sort']

    /** Sort order */
    order: NotificationListParams['order']
  }

  /** Real-time connection status */
  realtime: {
    /** Pusher connection status */
    connected: boolean

    /** Last connection attempt */
    lastConnected: string | null

    /** Connection error */
    error: string | null

    /** Reconnection attempts */
    reconnectAttempts: number
  }
}

/**
 * Notification action types for reducer
 */
export enum NotificationActionType {
  // Data loading
  FETCH_START = 'FETCH_START',
  FETCH_SUCCESS = 'FETCH_SUCCESS',
  FETCH_ERROR = 'FETCH_ERROR',
  FETCH_MORE_START = 'FETCH_MORE_START',
  FETCH_MORE_SUCCESS = 'FETCH_MORE_SUCCESS',
  FETCH_MORE_ERROR = 'FETCH_MORE_ERROR',

  // Individual notification actions
  MARK_AS_READ_START = 'MARK_AS_READ_START',
  MARK_AS_READ_SUCCESS = 'MARK_AS_READ_SUCCESS',
  MARK_AS_READ_ERROR = 'MARK_AS_READ_ERROR',

  DELETE_START = 'DELETE_START',
  DELETE_SUCCESS = 'DELETE_SUCCESS',
  DELETE_ERROR = 'DELETE_ERROR',

  // Bulk operations
  MARK_ALL_AS_READ_START = 'MARK_ALL_AS_READ_START',
  MARK_ALL_AS_READ_SUCCESS = 'MARK_ALL_AS_READ_SUCCESS',
  MARK_ALL_AS_READ_ERROR = 'MARK_ALL_AS_READ_ERROR',

  // Rollback actions (for optimistic update failures)
  MARK_AS_READ_ROLLBACK = 'MARK_AS_READ_ROLLBACK',
  MARK_ALL_AS_READ_ROLLBACK = 'MARK_ALL_AS_READ_ROLLBACK',
  DELETE_ROLLBACK = 'DELETE_ROLLBACK',

  // Real-time updates
  REALTIME_NOTIFICATION_RECEIVED = 'REALTIME_NOTIFICATION_RECEIVED',
  REALTIME_NOTIFICATION_READ = 'REALTIME_NOTIFICATION_READ',
  REALTIME_NOTIFICATION_DELETED = 'REALTIME_NOTIFICATION_DELETED',
  REALTIME_BULK_READ = 'REALTIME_BULK_READ',

  // Connection management
  REALTIME_CONNECTED = 'REALTIME_CONNECTED',
  REALTIME_DISCONNECTED = 'REALTIME_DISCONNECTED',
  REALTIME_ERROR = 'REALTIME_ERROR',

  // Unread count
  UPDATE_UNREAD_COUNT = 'UPDATE_UNREAD_COUNT',

  // Error handling
  CLEAR_ERROR = 'CLEAR_ERROR',
  SET_ERROR = 'SET_ERROR',

  // System actions
  RESET = 'RESET',
  CLEAR_NOTIFICATIONS = 'CLEAR_NOTIFICATIONS',
}

/**
 * Notification actions for reducer
 */
export type NotificationAction =
  | { type: NotificationActionType.FETCH_START }
  | {
      type: NotificationActionType.FETCH_SUCCESS
      payload: {
        notifications: Notification[]
        pagination: Partial<NotificationState['pagination']>
      }
    }
  | {
      type: NotificationActionType.FETCH_ERROR
      payload: { error: string; code?: string; retryable?: boolean }
    }
  | { type: NotificationActionType.FETCH_MORE_START }
  | {
      type: NotificationActionType.FETCH_MORE_SUCCESS
      payload: {
        notifications: Notification[]
        pagination: Partial<NotificationState['pagination']>
      }
    }
  | {
      type: NotificationActionType.FETCH_MORE_ERROR
      payload: { error: string; code?: string; retryable?: boolean }
    }
  | {
      type: NotificationActionType.MARK_AS_READ_START
      payload: { notificationId: string }
    }
  | {
      type: NotificationActionType.MARK_AS_READ_SUCCESS
      payload: { notification: Notification }
    }
  | {
      type: NotificationActionType.MARK_AS_READ_ERROR
      payload: {
        error: string
        notificationId: string
        code?: string
        retryable?: boolean
      }
    }
  | {
      type: NotificationActionType.DELETE_START
      payload: { notificationId: string }
    }
  | {
      type: NotificationActionType.DELETE_SUCCESS
      payload: { notificationId: string }
    }
  | {
      type: NotificationActionType.DELETE_ERROR
      payload: {
        error: string
        notificationId: string
        code?: string
        retryable?: boolean
      }
    }
  | { type: NotificationActionType.MARK_ALL_AS_READ_START }
  | {
      type: NotificationActionType.MARK_ALL_AS_READ_SUCCESS
      payload: { count: number; message: string }
    }
  | {
      type: NotificationActionType.MARK_ALL_AS_READ_ERROR
      payload: { error: string; code?: string; retryable?: boolean }
    }
  | {
      type: NotificationActionType.MARK_AS_READ_ROLLBACK
      payload: { notification: Notification }
    }
  | {
      type: NotificationActionType.MARK_ALL_AS_READ_ROLLBACK
      payload: {
        notifications: Notification[]
        pagination: NotificationState['pagination']
      }
    }
  | {
      type: NotificationActionType.DELETE_ROLLBACK
      payload: {
        notifications: Notification[]
        pagination: NotificationState['pagination']
      }
    }
  | {
      type: NotificationActionType.REALTIME_NOTIFICATION_RECEIVED
      payload: { notification: Notification }
    }
  | {
      type: NotificationActionType.REALTIME_NOTIFICATION_READ
      payload: { notificationId: string; readAt: string }
    }
  | {
      type: NotificationActionType.REALTIME_NOTIFICATION_DELETED
      payload: { notificationId: string }
    }
  | {
      type: NotificationActionType.REALTIME_BULK_READ
      payload: { notificationIds: string[]; updatedCount: number }
    }
  | { type: NotificationActionType.REALTIME_CONNECTED }
  | { type: NotificationActionType.REALTIME_DISCONNECTED }
  | { type: NotificationActionType.REALTIME_ERROR; payload: { error: string } }
  | {
      type: NotificationActionType.UPDATE_UNREAD_COUNT
      payload: { count: number }
    }
  | { type: NotificationActionType.CLEAR_ERROR }
  | {
      type: NotificationActionType.SET_ERROR
      payload: { error: string; code?: string; retryable?: boolean }
    }
  | { type: NotificationActionType.RESET }
  | { type: NotificationActionType.CLEAR_NOTIFICATIONS }

// ================================
// ERROR HANDLING TYPES
// ================================

/**
 * Standard error response from the API
 */
export interface NotificationApiError {
  /** Error message */
  message: string

  /** Error code */
  code: string

  /** HTTP status code */
  status: number

  /** Whether the request can be retried */
  retryable: boolean

  /** Additional error details */
  details?: Record<string, unknown>
}

/**
 * Network error types
 */
export enum NetworkErrorType {
  TIMEOUT = 'TIMEOUT',
  NETWORK_ERROR = 'NETWORK_ERROR',
  RATE_LIMITED = 'RATE_LIMITED',
  UNAUTHORIZED = 'UNAUTHORIZED',
  FORBIDDEN = 'FORBIDDEN',
  NOT_FOUND = 'NOT_FOUND',
  SERVER_ERROR = 'SERVER_ERROR',
  UNKNOWN = 'UNKNOWN',
}

/**
 * Structured error with additional context
 */
export interface NotificationError extends Error {
  /** Error code */
  code: string

  /** Whether the operation can be retried */
  retryable: boolean

  /** HTTP status code (if applicable) */
  status?: number

  /** Original error */
  originalError?: Error

  /** Additional context */
  context?: Record<string, unknown>
}

// ================================
// UTILITY TYPES
// ================================

/**
 * Priority level mapping for display
 */
export interface PriorityConfig {
  label: string
  color: string
  icon?: string
}

/**
 * Priority level configurations
 */
export type PriorityConfigMap = Record<NotificationPriority, PriorityConfig>

/**
 * Notification type configuration for display
 */
export interface NotificationTypeConfig {
  label: string
  description: string
  icon: React.ComponentType<{ sx?: object }> | FluentIcon
  iconColor: string
  bgColor?: string
}

/**
 * Notification type configurations
 */
export type NotificationTypeConfigMap = Record<
  NotificationType,
  NotificationTypeConfig
>

/**
 * Date formatting options
 */
export interface DateFormatOptions {
  /** Show relative time (e.g., "2 hours ago") */
  relative?: boolean

  /** Include time in absolute format */
  includeTime?: boolean

  /** Timezone to use */
  timezone?: string
}

// ================================
// HOOKS AND CONTEXT TYPES
// ================================

/**
 * Notification context interface
 */
export interface NotificationContextValue {
  /** Current state */
  state: NotificationState

  /** Actions */
  actions: {
    /** Fetch notifications with optional params */
    fetchNotifications: (params?: NotificationListParams) => Promise<void>

    /** Load more notifications (pagination) */
    loadMore: () => Promise<void>

    /** Mark notification as read */
    markAsRead: (id: string) => Promise<void>

    /** Mark all notifications as read */
    markAllAsRead: () => Promise<void>

    /** Delete notification */
    deleteNotification: (id: string) => Promise<void>

    /** Clear error state */
    clearError: () => void

    /** Refresh notifications */
    refresh: () => Promise<void>

    /** Disconnect and reset notification system (used on sign-out) */
    disconnect: () => void
  }

  /** Utility functions */
  utils: {
    /** Get notification by ID */
    getNotificationById: (id: string) => Notification | undefined

    /** Get unread notifications */
    getUnreadNotifications: () => Notification[]

    /** Check if notification is expired */
    isExpired: (notification: Notification) => boolean

    /** Format notification date */
    formatDate: (date: string, options?: DateFormatOptions) => string

    /** Get type configuration */
    getTypeConfig: (type: NotificationType) => NotificationTypeConfig
  }
}

/**
 * Custom hook return type for notifications
 */
export type UseNotificationsReturn = NotificationContextValue

/**
 * Custom hook return type for unread count
 */
export interface UseUnreadCountReturn {
  /** Current unread count */
  count: number

  /** Whether the count is loading */
  loading: boolean

  /** Error if any */
  error: string | null

  /** Refresh the count */
  refresh: () => Promise<void>
}

/**
 * Custom hook return type for Pusher subscription
 */
export interface UsePusherSubscriptionReturn {
  /** Connection status */
  connected: boolean

  /** Connection error */
  error: string | null

  /** Reconnect manually */
  reconnect: () => void

  /** Disconnect */
  disconnect: () => void
}

// ================================
// PUSHER INTEGRATION TYPES
// ================================

/**
 * Converts Pusher NotificationData to our Notification interface
 * Handles type conversion and provides defaults for missing fields
 * Logs warning if notification type is not recognized
 */
export function pusherToNotification(data: NotificationData): Notification {
  // Validate notification type
  if (!isKnownNotificationType(data.type)) {
    // eslint-disable-next-line no-console
    console.warn(
      `⚠️  Unknown notification type received: "${data.type}"`,
      '\nThis type is not in EventPayloadId enum and will use fallback display config.',
      '\nNotification data:',
      data
    )
  }

  return {
    id: data.id,
    type: data.type as NotificationType,
    title: data.title,
    content: data.content,
    metadata: data.metadata || {},
    status: 'unread' as const,
    read_at: null,
    created_at:
      typeof data.created_at === 'string'
        ? data.created_at
        : data.created_at.toISOString(),
    expires_at: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(), // Default 30 days
    source_type: (data.metadata?.source_type as string) || null,
    source_id: (data.metadata?.source_id as string) || null,
    priority: (data.metadata?.priority as NotificationPriority) || 1,
  }
}

/**
 * Converts Pusher NotificationReadData to the format expected by our reducer
 */
export function pusherReadToAction(data: NotificationReadData): {
  notificationId: string
  readAt: string
} {
  return {
    notificationId: data.id,
    readAt:
      typeof data.read_at === 'string'
        ? data.read_at
        : data.read_at.toISOString(),
  }
}

/**
 * Converts Pusher UnreadCountData to the format expected by our reducer
 */
export function pusherUnreadCountToAction(data: UnreadCountData): {
  count: number
  updatedAt: string
} {
  return {
    count: data.unread_count,
    updatedAt:
      typeof data.updated_at === 'string'
        ? data.updated_at
        : data.updated_at.toISOString(),
  }
}
